import {
  RuteroServiceMoment,
  ruteroServiceSchedule,
} from '@bandido/shared/types';

import type { TravelBooking } from './queries';
import { routeMapMode, type RouteMapPoint } from './routeMap';

export interface OperationalServiceMomentView {
  direction: 'start' | 'end';
  date: string;
  time?: string;
  timeZone?: string;
  label?: string;
  address?: string;
  point?: RouteMapPoint;
  source: 'provider' | 'request';
  legacy: boolean;
}

function safeMoment(value: unknown): RuteroServiceMoment | undefined {
  const reading = ruteroServiceSchedule({ start: value } as never);
  if (reading.status !== 'resolved') return undefined;
  return {
    ...reading.schedule,
    date: reading.schedule.date as string,
    source: reading.source,
  };
}

function persistedView(
  direction: OperationalServiceMomentView['direction'],
  value: unknown,
): OperationalServiceMomentView | undefined {
  const moment = safeMoment(value);
  if (!moment) return undefined;
  const point: RouteMapPoint | undefined = moment.point
    ? {
      lat: moment.point.lat,
      lng: moment.point.lng,
      ...((moment.label ?? moment.point.name)
        ? { label: moment.label ?? moment.point.name }
        : {}),
      ...((moment.address ?? moment.point.address)
        ? { address: moment.address ?? moment.point.address }
        : {}),
      source: moment.source === 'provider' ? 'resolved' : 'requested',
    }
    : undefined;
  return {
    direction,
    date: moment.date,
    ...(moment.time ? { time: moment.time } : {}),
    ...(moment.timeZone ? { timeZone: moment.timeZone } : {}),
    ...((moment.label ?? moment.point?.name)
      ? { label: moment.label ?? moment.point?.name }
      : {}),
    ...((moment.address ?? moment.point?.address)
      ? { address: moment.address ?? moment.point?.address }
      : {}),
    ...(point ? { point } : {}),
    source: moment.source,
    legacy: false,
  };
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function legacyView(
  booking: TravelBooking,
  direction: OperationalServiceMomentView['direction'],
): OperationalServiceMomentView | undefined {
  const isStart = direction === 'start';
  const date = text(isStart ? booking.serviceDate : booking.serviceEndDate);
  if (!date) return undefined;
  const routePoint =
    booking.routePoints?.[isStart ? 0 : 1] ??
    (!isStart ? booking.routePoints?.[0] : undefined);
  const fare = booking.fareSnapshot;
  const snapshotLabel = text(
    isStart ? fare?.pickUpLocation : fare?.dropOffLocation,
  );
  return {
    direction,
    date,
    ...(text(isStart ? booking.serviceTime : booking.serviceEndTime)
      ? { time: text(isStart ? booking.serviceTime : booking.serviceEndTime) }
      : {}),
    ...((text(routePoint?.label) ?? snapshotLabel)
      ? { label: text(routePoint?.label) ?? snapshotLabel }
      : {}),
    ...(text(routePoint?.address)
      ? { address: text(routePoint?.address) }
      : {}),
    ...(routePoint ? { point: routePoint } : {}),
    source: routePoint?.source === 'resolved' ? 'provider' : 'request',
    legacy: true,
  };
}

/** Momento autoritativo, con fallback explícito para reservas históricas. */
export function operationalServiceMomentViews(
  booking: TravelBooking,
): OperationalServiceMomentView[] {
  return (['start', 'end'] as const).flatMap((direction) => {
    const persisted = persistedView(
      direction,
      direction === 'start'
        ? booking.serviceStartMoment
        : booking.serviceEndMoment,
    );
    const legacy = legacyView(booking, direction);
    if (!persisted) {
      return [legacy].filter(Boolean) as OperationalServiceMomentView[];
    }
    const hasOperationalLocation = Boolean(
      persisted.label || persisted.address || persisted.point,
    );
    if (hasOperationalLocation || !legacy) return [persisted];
    return [
      {
        ...persisted,
        ...(legacy.label ? { label: legacy.label } : {}),
        ...(legacy.address ? { address: legacy.address } : {}),
        ...(legacy.point ? { point: legacy.point } : {}),
        source: legacy.source,
        legacy: true,
      },
    ];
  });
}

export function operationalServiceMapPoints(
  booking: TravelBooking,
): RouteMapPoint[] {
  const points = operationalServiceMomentViews(booking)
    .map((moment) => moment.point)
    .filter((point): point is RouteMapPoint => Boolean(point));
  if (routeMapMode(booking.vertical, points)) return points;

  const savedRoute = booking.routePoints ?? [];
  return routeMapMode(booking.vertical, savedRoute) ? savedRoute : [];
}
